<bit-chip-content size="large">
  <button
    bit-chip-content
    [startIcon]="icon()"
    [endIcon]="active() ? undefined : menuTrigger.isOpen() ? 'bwi-angle-up' : 'bwi-angle-down'"
    type="button"
    class="tw-w-full tw-truncate tw-border-none tw-bg-transparent tw-text-[length:inherit] tw-text-[color:inherit] tw-outline-none hover:tw-bg-transparent"
    [class.tw-cursor-not-allowed]="disabled()"
    [bitMenuTriggerFor]="menu"
    [attr.aria-disabled]="disabled() || null"
    [class.tw-pointer-events-auto]="showDisabledReason()"
    [bitTooltip]="triggerTooltip()"
    [addTooltipToDescribedby]="showDisabledReason()"
    #menuTrigger="menuTrigger"
    #chipTrigger
  >
    <span class="tw-block tw-min-w-0 tw-truncate">{{ displayLabel() }}</span>
  </button>
  <bit-berry slot="end" [value]="committedCount()"></bit-berry>
  @if (active()) {
    <button
      bit-chip-dismiss-button
      slot="end"
      class="tw-relative tw-bottom-[1px]"
      type="button"
      [attr.aria-label]="'removeItem' | i18n: displayLabel()"
      [disabled]="disabled()"
      (click)="clearFromChip()"
    ></button>
  }
</bit-chip-content>

<!-- Instantiates projected content so the chip can draw rows from it; data-driven options need no
     such trick, since they're plain rows built from `options()`, never stamped as components. -->
<div class="tw-hidden">
  <ng-content></ng-content>
</div>

<!-- Single-select rows are flat: nesting requires `multiple`, enforced in the component. -->
<ng-template #optionRow let-option>
  <div data-filter-option-row [class]="optionRowClasses">
    <!-- Tooltip on the row, not the label span: the span isn't focusable, and `focusin` from the
         radio bubbles up here, so the full name is also reachable by keyboard. -->
    <label
      class="tw-mb-0 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2"
      [bitTooltip]="option.label()"
    >
      <input
        type="radio"
        [class]="radioInputClasses"
        [attr.name]="radioName"
        [checked]="optionSelected(option)"
        [disabled]="option.disabled()"
        (change)="toggleOption(option)"
      />
      @if (option.iconTile(); as tile) {
        <!-- Flex on the host: an inline host adds descender space below the tile, pushing it
             above the row's center. -->
        <bit-icon-tile
          class="!tw-flex tw-flex-none"
          [icon]="tile.icon"
          [variant]="tileVariant(option)"
          [color]="tileColor(option)"
          [emphasis]="tile.emphasis ?? 'subtle'"
          size="xs"
        />
      }
      <span class="tw-min-w-0 tw-flex-1 tw-truncate">{{ option.label() }}</span>
      @let count = optionCounts().get(option);
      @if (count != null) {
        <span class="tw-flex-none tw-text-xs tw-font-normal tw-text-fg-body-subtle">{{
          count
        }}</span>
      }
    </label>
  </div>
</ng-template>

<ng-template #noResultsState>
  <bit-status-lockup size="small">
    <bit-icon
      slot="graphic"
      name="bwi-search"
      class="tw-text-2xl tw-text-fg-body-subtle"
    ></bit-icon>
    <span slot="title">{{ "noFiltersMatchTerm" | i18n: searchTerm() }}</span>
    <button
      slot="button"
      bitButton
      buttonType="secondary"
      size="small"
      type="button"
      startIcon="bwi-clear"
      (click)="clearSearch($event)"
    >
      {{ "clearSearch" | i18n }}
    </button>
  </bit-status-lockup>
</ng-template>

<ng-template #optionsBody>
  @if (showSearch()) {
    <div data-filter-search-row class="tw-px-2 tw-pb-1">
      <bit-search
        data-filter-search
        [ngModel]="searchTerm()"
        (ngModelChange)="setSearchTerm($event)"
      ></bit-search>
    </div>
  }
  @if (resultCount() != null) {
    <div
      data-filter-result-count
      class="tw-px-4 tw-pb-1 tw-text-xs tw-font-normal tw-text-fg-body-subtle"
      aria-live="polite"
    >
      {{
        resultCount() === 1 ? ("oneFilterResult" | i18n) : ("filterResults" | i18n: resultCount())
      }}
    </div>
  }
  @if (multiple()) {
    <div
      role="tree"
      aria-multiselectable="true"
      [attr.aria-label]="placeholderText()"
      tabindex="-1"
      class="tw-min-h-0 tw-flex-1 tw-overflow-y-auto"
      data-filter-card-list
      (keydown)="onTreeKeydown($event)"
    >
      @for (node of treeNodes(); track $index) {
        @let expandable = node.row.expandable();
        @let checked = nodeChecked(node);
        @if (node.dividerBefore) {
          <!-- The dialog hides this and separates the groups with a gap instead.
               `aria-hidden` because the rule is decorative and `role="tree"` owns only
               `treeitem`/`group` — the divider's own `role="separator"` would fail
               `aria-required-children` otherwise. -->
          <bit-menu-divider data-filter-divider aria-hidden="true"></bit-menu-divider>
        }
        <div
          role="treeitem"
          data-filter-option-row
          [attr.data-filter-section-row]="node.row.kind === 'section' ? '' : null"
          [attr.data-filter-card-top]="node.groupStart ? '' : null"
          [attr.data-filter-card-bottom]="node.groupEnd ? '' : null"
          [bitFilterTreeRow]="node"
          [class]="node.row.kind === 'option' ? optionRowClasses : rowBaseClasses"
          [style.padding-inline-start.rem]="
            node.row.kind === 'section' ? null : 0.5 + (node.indent - 1) * 1.75
          "
          [attr.aria-level]="node.level"
          [attr.aria-setsize]="node.setsize"
          [attr.aria-posinset]="node.posinset"
          [attr.aria-expanded]="expandable ? node.expanded : null"
          [attr.aria-checked]="checked"
          [attr.aria-disabled]="node.row.disabled() ? true : null"
          [bitTooltip]="node.row.label()"
          (click)="node.row.disabled() ? null : activateNode(node)"
          (focus)="onRowFocus(row)"
          #row="bitFilterTreeRow"
        >
          @if (node.row.kind !== "section") {
            @if (expandable) {
              <span
                data-filter-expander
                class="-tw-me-1 tw-flex tw-size-6 tw-flex-none tw-items-center tw-justify-center tw-text-fg-body-subtle"
                aria-hidden="true"
                (click)="node.row.toggleExpanded(); $event.stopPropagation()"
              >
                <!-- An option discloses children, so it takes the solid caret a nested
                     `bit-nav-group` uses: one glyph, rotated when collapsed, sized as that
                     group's `xsmall` icon button renders it. -->
                <bit-icon
                  name="bwi-down-solid"
                  class="tw-text-xl"
                  [class.-tw-rotate-90]="!node.expanded"
                ></bit-icon>
              </span>
            } @else if (node.reserveExpander) {
              <span class="-tw-me-1 tw-size-6 tw-flex-none" aria-hidden="true"></span>
            }
          }
          @if (checked !== null) {
            <!-- Draws the control only; the row owns the semantics via `aria-checked`. -->
            <input
              type="checkbox"
              bitCheckbox
              class="tw-pointer-events-none"
              aria-hidden="true"
              tabindex="-1"
              [checked]="checked === 'true'"
              [indeterminate]="checked === 'mixed'"
              [disabled]="node.row.disabled()"
            />
          }
          @if (asOption(node.row); as option) {
            @let tile = option.iconTile();
            @if (tile) {
              <bit-icon-tile
                class="!tw-flex tw-flex-none"
                [icon]="tile.icon"
                [variant]="tileVariant(option)"
                [color]="tileColor(option)"
                [emphasis]="tile.emphasis ?? 'subtle'"
                size="xs"
              />
            }
          }
          <span
            data-filter-row-label
            class="tw-min-w-0 tw-flex-1 tw-truncate"
            [class.tw-font-semibold]="node.row.kind === 'section'"
            >{{ node.row.label() }}</span
          >
          @if (asSection(node.row); as section) {
            <bit-berry [value]="sectionSelectedCount(section)"></bit-berry>
            @if (expandable) {
              <span
                data-filter-expander
                class="tw-ms-1 tw-flex tw-flex-none tw-items-center tw-text-fg-body-subtle"
                aria-hidden="true"
                (click)="node.row.toggleExpanded(); $event.stopPropagation()"
              >
                <bit-icon
                  [name]="node.expanded ? 'bwi-angle-up' : 'bwi-angle-down'"
                  class="tw-text-xs"
                ></bit-icon>
              </span>
            }
          } @else if (asOption(node.row); as option) {
            @let count = optionCounts().get(option);
            @if (count != null) {
              <span class="tw-flex-none tw-text-xs tw-font-normal tw-text-fg-body-subtle">{{
                count
              }}</span>
            }
          }
        </div>
      }
    </div>
    @if (noResults()) {
      <ng-container *ngTemplateOutlet="noResultsState"></ng-container>
    }
  } @else {
    <div
      role="group"
      [attr.aria-label]="placeholderText()"
      class="tw-min-h-0 tw-flex-1 tw-overflow-y-auto"
      data-filter-option-list
    >
      @if (!multiple()) {
        @let allSelected = isSelected(clearValue);
        <label
          data-filter-option-row
          [class]="optionRowClasses"
          [bitTooltip]="unsetLabel() ?? ('all' | i18n)"
        >
          <input
            type="radio"
            [class]="radioInputClasses"
            [attr.name]="radioName"
            [checked]="allSelected"
            (change)="toggle(clearValue)"
          />
          <span class="tw-min-w-0 tw-flex-1 tw-truncate">{{ unsetLabel() ?? ("all" | i18n) }}</span>
          @if (unsetCount() != null) {
            <span class="tw-flex-none tw-text-xs tw-font-normal tw-text-fg-body-subtle">{{
              unsetCount()
            }}</span>
          }
        </label>
      }

      @for (entry of entries(); track $index) {
        @if (asSection(entry); as section) {
          @if (rowVisible(section)) {
            @if (section.collapsible()) {
              <button
                type="button"
                class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-none tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-start tw-text-sm tw-font-semibold tw-text-main hover:tw-bg-secondary-100 focus-visible:tw-z-10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-border-focus"
                [attr.aria-expanded]="section.open()"
                [bitTooltip]="section.label()"
                (click)="section.toggleExpanded()"
              >
                <span class="tw-min-w-0 tw-flex-1 tw-truncate">{{ section.label() }}</span>
                <bit-berry [value]="sectionSelectedCount(section)"></bit-berry>
                <bit-icon
                  [name]="section.open() ? 'bwi-angle-up' : 'bwi-angle-down'"
                  class="tw-flex-none tw-text-muted"
                  aria-hidden="true"
                ></bit-icon>
              </button>
            } @else {
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-muted"
                [bitTooltip]="section.label()"
              >
                <span class="tw-min-w-0 tw-flex-1 tw-truncate">{{ section.label() }}</span>
                <bit-berry [value]="sectionSelectedCount(section)"></bit-berry>
              </div>
            }
            @if (section.open()) {
              @for (option of section.children(); track option.value()) {
                @if (rowVisible(option)) {
                  <ng-container
                    *ngTemplateOutlet="optionRow; context: { $implicit: option }"
                  ></ng-container>
                }
              }
            }
          }
        } @else if (entry.kind === "divider") {
          <!-- Gated so a search can't leave a rule with nothing on one side of it. -->
          @if (dividerVisible($index)) {
            <bit-menu-divider data-filter-divider></bit-menu-divider>
          }
        } @else if (asOption(entry); as option) {
          @if (rowVisible(option)) {
            <ng-container
              *ngTemplateOutlet="optionRow; context: { $implicit: option }"
            ></ng-container>
          }
        }
      }

      @if (noResults()) {
        <ng-container *ngTemplateOutlet="noResultsState"></ng-container>
      }
    </div>
  }
</ng-template>

<bit-menu #menu ariaRole="dialog" [ariaLabel]="placeholderText()" (closed)="onMenuClosed()">
  <div class="tw-flex tw-max-h-96 tw-min-w-52 tw-max-w-80 tw-flex-col">
    <!-- Gated on open: the overlay keeps this view alive between openings, so ungated rows would
         outlive their popover and still count as the menu's once the dialog owns the options. -->
    @if (menuTrigger.isOpen()) {
      <ng-container *ngTemplateOutlet="optionsBody"></ng-container>
    }
  </div>
  @if (multiple() && active()) {
    <div slot="footer" class="tw-flex tw-items-center tw-gap-2">
      <span class="tw-flex-1 tw-text-xs tw-font-normal tw-text-fg-body-subtle">{{
        "filtersSelected" | i18n: selectedCount()
      }}</span>
      <button
        bitButton
        buttonType="subtleGhost"
        size="small"
        type="button"
        startIcon="bwi-clear"
        (click)="clearFromMenu($event)"
      >
        {{ "clear" | i18n }}
      </button>
    </div>
  }
</bit-menu>
